import { AccountDetailsFormValues } from '@account/types';
import { Roles } from '@common/types/user';
import { InputField } from '@components/utility/inputs';
import { Select } from '@components/utility/selects';
import { COUNTRIES_WITH_STATES } from '@constants/common';
import { SelectChangeEvent } from '@mui/material';
import { useGetCountriesQuery } from '@services/countries';
import { useGetStatesQuery } from '@services/states';
import { renderCountryOption, renderCountryValue } from '@utils/common';
import { useFormikContext } from 'formik';
import { useIntl } from 'react-intl';

export const LocationInformation = ({ loading }: { loading?: boolean }) => {
    //#region Hooks
    const intl = useIntl();
    const { values, errors, touched, handleChange } = useFormikContext<AccountDetailsFormValues>();
    //#endregion

    //#region Data Fetching
    const { data: countries, isLoading: isCountriesDataLoading } = useGetCountriesQuery();
    const { data: states, isLoading: isStatesDataLoading } = useGetStatesQuery(
        { country: values.country_code ?? '' },
        {
            skip: !values.country_code || !COUNTRIES_WITH_STATES.includes(values.country_code),
            selectFromResult: (response) => ({
                ...response,
                data: response.data?.map((state) => ({ label: state.name, value: state.name })) ?? [],
            }),
        }
    );
    //#endregion

    //#region Handlers
    const handleCountryChange = (e: SelectChangeEvent<unknown>) => {
        handleChange(e);
        // Reset state when country changes
        if (values.state_code) {
            handleChange({
                target: { name: 'state_code', value: '' },
            } as React.ChangeEvent<HTMLInputElement>);
        }
    };
    //#endregion

    return (
        <>
            <InputField
                id="address1"
                name="address1"
                buffered
                placeholder={intl.formatMessage({
                    id: 'common.address.placeholder',
                    defaultMessage: 'Enter full address',
                })}
                fullWidth
                required={values.role === Roles.INSTALLER}
                topLabel={intl.formatMessage({ id: 'common.address1', defaultMessage: 'Address 1' })}
                debounceMs={0}
                boxProps={{
                    className: 'md:col-span-2',
                }}
                loading={loading}
            />
            <InputField
                id="address2"
                name="address2"
                buffered
                placeholder={intl.formatMessage({
                    id: 'common.address.placeholder',
                    defaultMessage: 'Enter full address',
                })}
                fullWidth
                required={values.role === Roles.INSTALLER}
                topLabel={intl.formatMessage({ id: 'common.address2', defaultMessage: 'Address 2' })}
                debounceMs={0}
                boxProps={{
                    className: 'md:col-span-2',
                }}
                loading={loading}
            />
            <Select
                id="country_code"
                required
                items={countries ?? []}
                type="radio"
                placeholder={intl.formatMessage({
                    id: 'common.country.placeholder',
                    defaultMessage: 'Choose a country',
                })}
                size="smallest"
                topLabel={intl.formatMessage({ id: 'common.country', defaultMessage: 'Country' })}
                name="country_code"
                value={values.country_code ?? ''}
                error={touched.country_code && !!errors.country_code}
                helperText={touched.country_code && errors.country_code ? errors.country_code : undefined}
                onChange={handleCountryChange}
                fullWidth
                boxProps={{
                    className: 'md:col-span-2 w-full',
                }}
                searchable
                virtualize
                renderOption={renderCountryOption}
                renderValue={(value) => renderCountryValue(value as string, countries ?? [])}
                loading={isCountriesDataLoading || loading}
                loadingType="skeleton"
            />
            <Select
                id="state_code"
                items={states ?? []}
                type="radio"
                placeholder={intl.formatMessage({
                    id: 'common.state.placeholder',
                    defaultMessage: 'Choose a State / Province',
                })}
                size="smallest"
                topLabel={intl.formatMessage({ id: 'common.state', defaultMessage: 'State / Province' })}
                name="state_code"
                value={values.state_code ?? ''}
                error={touched.state_code && !!errors.state_code}
                helperText={touched.state_code && errors.state_code ? errors.state_code : undefined}
                onChange={handleChange}
                fullWidth
                boxProps={{
                    className: 'md:col-span-2 w-full',
                }}
                disabled={isStatesDataLoading || states?.length === 0}
                loading={isStatesDataLoading || loading}
                loadingType="skeleton"
            />
            <InputField
                id="city"
                name="city"
                buffered
                placeholder={intl.formatMessage({
                    id: 'common.city.placeholder',
                    defaultMessage: 'Enter city',
                })}
                fullWidth
                required
                topLabel={intl.formatMessage({ id: 'common.city', defaultMessage: 'City' })}
                debounceMs={0}
                boxProps={{
                    className: 'md:col-span-2',
                }}
                loading={loading}
            />
        </>
    );
};
